import { useEffect, useRef } from "react";
import { useArticles } from "@/contexts/ArticlesContext";
import { getArticleUrl } from "@/lib/articleUrls";
import { toast } from "sonner";

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
const SESSION_KEY_PREFIX = "breaking_shown_";

/**
 * Watches for articles tagged "breaking" that are less than 2 hours old.
 * Shows a non-blocking toast notification once per article per session.
 * Auto-dismisses after 8 seconds.
 */
const BreakingNewsToast = () => {
  const { articles } = useArticles();
  const shown = useRef(false);

  useEffect(() => {
    if (shown.current || articles.length === 0) return;
    shown.current = true;

    const now = Date.now();

    const breakingArticles = articles.filter((a) => {
      if (a.tag !== "breaking") return false;

      // Parse relative dates like "X mins ago", "X hours ago"
      let publishedMs = 0;
      const dateStr = a.date;
      if (dateStr.includes("min")) {
        const mins = parseInt(dateStr) || 0;
        publishedMs = now - mins * 60 * 1000;
      } else if (dateStr.includes("hour")) {
        const hours = parseInt(dateStr) || 0;
        publishedMs = now - hours * 60 * 60 * 1000;
      } else if (dateStr === "Just now") {
        publishedMs = now;
      } else {
        // Absolute date string
        publishedMs = new Date(dateStr).getTime();
      }

      if (isNaN(publishedMs)) return false;
      return now - publishedMs < TWO_HOURS_MS;
    });

    for (const article of breakingArticles) {
      const key = `${SESSION_KEY_PREFIX}${article.slug}`;
      if (sessionStorage.getItem(key)) continue;

      sessionStorage.setItem(key, "1");

      toast("Breaking News", {
        description: article.title,
        duration: 8000,
        className:
          "!border-destructive/40 !bg-destructive/10 !text-destructive [&>[data-content]]:!text-destructive [&_[data-description]]:!text-destructive/90",
        action: {
          label: "Read",
          onClick: () => {
            window.location.href = getArticleUrl(article);
          },
        },
      });
    }
  }, [articles]);

  return null;
};

export default BreakingNewsToast;
